import { z } from "zod";

import { iconNames } from "./iconRegistry";

export const iconNameSchema = z.enum(iconNames);

export const ctaLinkSchema = z.object({
  label: z.string().min(1),
  href: z.string().min(1),
  external: z.boolean().default(false),
  icon: iconNameSchema.optional(),
});

export const simpleLinkSchema = z.object({
  label: z.string().min(1),
  href: z.string().min(1),
  external: z.boolean().default(false),
});

export const pageSeoSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  canonicalPath: z.string().min(1),
  socialImage: z.string().min(1),
});

export const badgeSchema = z.object({
  icon: iconNameSchema,
  title: z.string().min(1),
  description: z.string().min(1),
});

export const heroMediaSchema = z
  .object({
    type: z.enum(["image", "mockup"]).default("image"),
    imageSrc: z.string().optional(),
    imageAlt: z.string().optional(),
    imageFit: z.enum(["cover", "contain"]).optional(),
    floatingBadge: badgeSchema.optional(),
    mockupIcon: iconNameSchema.optional(),
    mockupLabel: z.string().optional(),
    mockupCaption: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.type === "image" && (!value.imageSrc || !value.imageAlt)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Image hero media requires `imageSrc` and `imageAlt`.",
      });
    }
    if (value.type === "mockup" && !value.mockupIcon) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Mockup hero media requires `mockupIcon`.",
      });
    }
  });

export const heroSectionSchema = z.object({
  eyebrow: z.string().min(1),
  title: z.string().min(1),
  accent: z.string().optional(),
  description: z.string().min(1),
  secondaryDescription: z.string().optional(),
  primaryCta: ctaLinkSchema.optional(),
  secondaryCta: ctaLinkSchema.optional(),
  detailRows: z.array(
    z.object({
      label: z.string().min(1),
      value: z.string().min(1),
    }),
  ).optional(),
  footerNote: z.string().optional(),
  align: z.enum(["split", "center"]).default("split"),
  background: z.enum(["plain", "muted", "gradient"]).default("plain"),
  media: heroMediaSchema.optional(),
});

export const cardItemSchema = z.object({
  icon: iconNameSchema,
  title: z.string().min(1),
  description: z.string().min(1),
  kicker: z.string().optional(),
  href: z.string().optional(),
  external: z.boolean().optional(),
  ctaLabel: z.string().optional(),
  tone: z.enum(["default", "dark", "gradient", "soft", "outline"]).optional(),
  size: z.enum(["standard", "wide", "full"]).optional(),
});

export const cardGridSectionSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  cards: z.array(cardItemSchema).min(1),
});

export const checklistSectionSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  items: z.array(z.string().min(1)).min(1),
  accentLine: z.string().optional(),
  visualCards: z.array(cardItemSchema).optional(),
});

export const faqItemSchema = z.object({
  question: z.string().min(1),
  answer: z.string().min(1),
});

export const faqSectionSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  items: z.array(faqItemSchema).min(1),
});

export const ctaBannerSectionSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  primaryCta: ctaLinkSchema,
  secondaryCta: ctaLinkSchema.optional(),
  icon: iconNameSchema.optional(),
  tone: z.enum(["light", "dark"]).default("light"),
});

export const listSectionSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  items: z.array(
    z.object({
      title: z.string().min(1),
      description: z.string().min(1),
      icon: iconNameSchema.optional(),
    }),
  ).min(1),
});

export const embedCardSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  embedHtml: z.string().optional(),
  embedUrl: z.string().optional(),
  cta: ctaLinkSchema.optional(),
  placeholderLabel: z.string().optional(),
  aspectRatio: z.enum(["video", "form"]).default("video"),
});

export const newsletterSectionSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  embed: embedCardSchema,
  primaryCta: ctaLinkSchema,
  secondaryCta: ctaLinkSchema.optional(),
});

export const videoSectionSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  featured: embedCardSchema,
  playlist: embedCardSchema.optional(),
  primaryCta: ctaLinkSchema,
});

export const workshopsListSectionSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  cards: z.array(
    z.object({
      status: z.string().optional(),
      title: z.string().min(1),
      description: z.string().min(1),
      details: z.array(z.string().min(1)).optional(),
      cta: ctaLinkSchema,
    }),
  ).min(1),
});

export const resourcesSectionSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  cards: z.array(
    z.object({
      icon: iconNameSchema,
      title: z.string().min(1),
      description: z.string().min(1),
      cta: ctaLinkSchema,
    }),
  ).min(1),
});

export const profileSectionSchema = z.object({
  title: z.string().min(1),
  name: z.string().min(1),
  role: z.string().min(1),
  description: z.string().min(1),
  icon: iconNameSchema,
  imageSrc: z.string().optional(),
  imageAlt: z.string().optional(),
  cta: ctaLinkSchema.optional(),
});

export const valuesSectionSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  values: z.array(
    z.object({
      title: z.string().min(1),
      description: z.string().min(1),
      icon: iconNameSchema,
    }),
  ).min(1),
});

export const contactFormFieldSchema = z.object({
  name: z.string().min(1),
  label: z.string().min(1),
  type: z.enum(["text", "email", "textarea"]),
  placeholder: z.string().min(1),
  required: z.boolean().default(true),
});

export const contactFormSectionSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  intro: z.string().optional(),
  submitLabel: z.string().min(1),
  successMessage: z.string().min(1),
  fields: z.array(contactFormFieldSchema).min(1),
});

export const siteConfigSchema = z.object({
  brandName: z.string().min(1),
  domain: z.string().min(1),
  email: z.string().email(),
  newsletterUrl: z.string().url(),
  beehiivSubscribeUrl: z.string().url(),
  beehiivPublicationUrl: z.string().url(),
  beehiivEmbedHtml: z.string().optional(),
  youtubeUrl: z.string().url(),
  youtubeFeaturedEmbedUrl: z.string().optional(),
  youtubePlaylistEmbedUrl: z.string().optional(),
  stripeUrl: z.string().url(),
  aiCareerAdvantageStripeUrl: z.string().url(),
  headshotImage: z.string().optional(),
  aiCareerAdvantageMockupImage: z.string().optional(),
  workshopDate: z.string().optional(),
  workshopTime: z.string().optional(),
  workshopRegistrationUrl: z.string().optional(),
  paymentUrl: z.string().optional(),
  contactFormEndpoint: z.string().optional(),
  navLinks: z.array(
    simpleLinkSchema,
  ),
  footerLinks: z.array(
    simpleLinkSchema,
  ),
  footerTagline: z.string().min(1),
  navPrimaryCta: ctaLinkSchema,
  navSecondaryCta: ctaLinkSchema,
  socialLinks: z.array(
    z.object({
      label: z.string().min(1),
      href: z.string().min(1),
      icon: iconNameSchema,
    }),
  ),
});
